import { Box, IconButton, Skeleton, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Icon } from '@verantos-dev/shared-components';

import { useAppDispatch, useAppSelector } from '../../../../../../lib/store';
import { triggerScrollToHighlight } from '../../../../../annotation/state/annotationSlice';
import type { Annotation, Span } from '../../../../../workspace-common/types/responseTypes';
import {
  selectSelectedNoteId,
  selectPendingSpansForAnnotation,
  selectAnnotationIdsInEditMode,
  selectEvidenceIdsToDelete,
} from '../../../../state/selector';
import {
  addHighlightedTextSpanFromSpan,
  removeHighlightedTextSpan,
  setSelectedNoteId,
  removePendingSpan,
  clearHighlightedTextSpans,
  addToEvidenceIdsToDelete,
} from '../../../../state/supervisorSlice';

interface EvidencesListProps {
  annotation: Annotation | null;
  conceptId: string;
  restrictEdit?: boolean;
  editingAnnotationId?: string;
  loading?: boolean;
}

export const AnnotatorEvidencesList = ({
  annotation,
  conceptId,
  editingAnnotationId,
  loading = false,
  restrictEdit = false,
}: EvidencesListProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const selectedNoteId = useAppSelector(selectSelectedNoteId);

  // For new annotations, use temporary annotationId; for existing, use actual annotationId
  const annotationId = annotation?.annotationId || editingAnnotationId || `new-${conceptId}`;

  const annotationIdsInEditMode = useAppSelector(selectAnnotationIdsInEditMode);

  const isInEdit = annotationIdsInEditMode.includes(annotationId);

  // Memoize the selector to avoid recreating it on every render
  const pendingSpansSelector = useMemo(
    () => selectPendingSpansForAnnotation(annotationId),
    [annotationId]
  );

  // Get pending spans (not yet saved) for this annotation
  const pendingSpans = useAppSelector(pendingSpansSelector);

  const evidenceIdsToDeleteMap = useAppSelector(selectEvidenceIdsToDelete);
  const evidenceIdsToDelete = evidenceIdsToDeleteMap[annotationId] || [];

  // Combine saved spans (from server) and pending spans (not yet saved)
  // For new annotations, there are no saved spans yet
  const allSpans: Array<Span & { isPending?: boolean }> = [
    ...(annotation?.spans
      .filter((span) => !evidenceIdsToDelete.includes(span.evidenceId!))
      .map((span) => ({ ...span, isPending: false })) || []),
    ...pendingSpans.map((span) => ({ ...span, isPending: true })),
  ];
  const showHighlightedText = (span: Span, number: number, spanAnnotationId: string) => {
    // First set the selected note
    dispatch(setSelectedNoteId(span.noteId));
    // Then add the span to highlightedTextSpans for highlighting
    // Clear any existing highlights for this note first, so only this span is highlighted
    dispatch(clearHighlightedTextSpans());
    dispatch(
      addHighlightedTextSpanFromSpan({
        span,
        number,
        annotationId: spanAnnotationId,
      })
    );
    dispatch(triggerScrollToHighlight());
  };

  // For pending spans, just set the selected note (don't add to highlightedTexts)
  const showPendingSpan = (noteId: string) => {
    dispatch(setSelectedNoteId(noteId));
    dispatch(triggerScrollToHighlight());
  };

  const handleDeleteSpan = (
    e: React.MouseEvent<HTMLButtonElement>,
    spanAnnotationId: string,
    evidenceId: string | undefined,
    noteId: string,
    offsetStart: number,
    offsetEnd: number,
    isPending: boolean
  ) => {
    e.preventDefault();

    if (isPending) {
      dispatch(
        removePendingSpan({ annotationId: spanAnnotationId, noteId, offsetStart, offsetEnd })
      );
      if (selectedNoteId === noteId) {
        dispatch(
          removeHighlightedTextSpan({
            offsetStart,
            offsetEnd,
            noteId,
            annotationId: spanAnnotationId,
          })
        );
      }
      return;
    }

    if (!evidenceId) return;
    dispatch(addToEvidenceIdsToDelete({ annotationId: spanAnnotationId, evidenceId }));
    if (selectedNoteId === noteId) {
      dispatch(
        removeHighlightedTextSpan({
          offsetStart,
          offsetEnd,
          noteId,
          annotationId: spanAnnotationId,
        })
      );
    }
  };

  return (
    <Box id="saved-evidence-container">
      <ul id="saved-evidence-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {allSpans.map((span, spanIndex: number) => {
          const isPending = span.isPending ?? false;
          if (loading) {
            return (
              <li
                key={`${span.noteId}-${span.offsetStart}-${span.offsetEnd}-${
                  isPending ? 'pending' : 'saved'
                }`}
              >
                <Skeleton variant="text" width="100%" height="50px" />
              </li>
            );
          }
          return (
            <li
              key={`${span.noteId}-${span.offsetStart}-${span.offsetEnd}-${
                isPending ? 'pending' : 'saved'
              }`}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: theme.spacing(1),
                  gap: theme.spacing(3),
                }}
              >
                <Box sx={{ textOverflow: 'ellipsis', overflow: 'hidden', flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontStyle: isPending ? 'italic' : 'normal',
                      color: isPending ? theme.palette.text.secondary : 'inherit',
                    }}
                  >
                    "{span.text}" {isPending && '(Pending)'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                  {isPending ? (
                    <Typography
                      onClick={() => showPendingSpan(span.noteId)}
                      variant="body2"
                      sx={{ color: theme.palette.primary.main, cursor: 'pointer' }}
                    >
                      {formatMessage({
                        id: 'workspaceCommon.savedAnnotationCard.showEvidenceButton',
                      })}
                    </Typography>
                  ) : (
                    <Typography
                      onClick={() => showHighlightedText(span, spanIndex + 1, annotationId)}
                      variant="body2"
                      sx={{ color: theme.palette.primary.main, cursor: 'pointer' }}
                    >
                      {formatMessage({
                        id: 'workspaceCommon.savedAnnotationCard.showEvidenceButton',
                      })}
                    </Typography>
                  )}
                  {!restrictEdit && (isInEdit || isPending) && (
                    <IconButton
                      onClick={(e) =>
                        handleDeleteSpan(
                          e,
                          annotationId,
                          span.evidenceId,
                          span.noteId,
                          span.offsetStart,
                          span.offsetEnd,
                          isPending
                        )
                      }
                    >
                      <Icon icon="clear" id="delete-evidence-icon" width={12} height={12} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </li>
          );
        })}
      </ul>
    </Box>
  );
};

export default AnnotatorEvidencesList;
